import dayjs, { Dayjs } from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(isSameOrBefore);
dayjs.extend(timezone);
dayjs.extend(utc);

/** ISO 8601 standard date format. */
export const globalDateFormat: string = "YYYY-MM-DD";

/** ISO 8601 standard date and time format. */
export const globalDateTimeFormat: string = `${globalDateFormat} HH:mm:ss`;

/** 12 hour time format */
export const global12HourTimeFormat: string = "h:mm A";

/** Default time zone to use when Fulfillment Center time zone is not provided. */
const defaultTimezone = "America/New_York";

/**
 * Format the given date as an ISO 8601 date without time.
 *
 * WARNING: JS treats dates as being in local browser time, so the
 * input date may not match the original FC time.
 * E.g. if the FC is in Mountain Time and the browser is in European Time,
 *      an off-by-one error may occur.
 *
 * @date The date to format.
 * */
export const formatDate = (date: Date | Dayjs): string =>
  dayjs(date).format(globalDateFormat);

/**
 * Format the given date and time as ISO 8601.
 *
 * @date The date and time to format.
 * @timeZone The time zone to use. Uses a default if not specified.
 * */
export const formatDateTime = (
  date: Date | null | undefined,
  timeZone?: string | null
): string =>
  date
    ? dayjs.tz(date, timeZone || defaultTimezone).format(globalDateTimeFormat)
    : "";

/**
 * Format the given date as an ISO 8601 date (YYYY-MM-DD) in the specified time zone.
 *
 * @param date The date to format.
 * @param timeZone The time zone to use. Uses a default (America/New_York) if not specified.
 */
export const formatDateForTimeZone = (
  date: Date | string | null | undefined,
  timeZone?: string | null
): string =>
  date
    ? dayjs.tz(date, timeZone || defaultTimezone).format(globalDateFormat)
    : "";

/**
 * Convert the given date as time to the specified time zone and format it as 12 hour time.
 *
 * @param date The date to format.
 * @param timeZone The time zone to use. Uses a default (America/New_York) if not specified.
 */
export const formatTimeInTimeZone = (
  date: Date | string | null | undefined,
  timeZone?: string | null
): string =>
  date
    ? dayjs(date)
        .tz(timeZone || defaultTimezone)
        .format(global12HourTimeFormat)
    : "";

/**
 * Take the given date, converts to UTC, and format just the date portion of it.
 * E.g. given 2022-04-05 13:43:21-05:00, will return "2022-04-05"
 * Or,  given 2022-04-05 00:00:00-00:00, will return "2022-04-05"
 *
 * NOTE: If the date is not already in UTC, this function may switch to a different date.
 * E.g. 2022-04-05 22:00:00-05:00 will return "2022-04-06" since that is the UTC date.
 *
 * @date The date.
 */
export const formatUtcDate = (
  date: Date | Dayjs | string | null | undefined
): string => (date ? dayjs.utc(date).format(globalDateFormat) : "");

/** Format a Dayjs object in ISO 8601 format, or null if null/undefined is passed in. */
export const formatDayjsMaybe = (
  dayjsDate: Dayjs | null | undefined
): string | null => dayjsDate?.format(globalDateFormat) ?? null;

/** Format a Dayjs object in ISO 8601 format, or empty string if null/undefined is passed in. */
export const formatDateDayjs = (
  date: Dayjs | string | null | undefined
): string => (date ? dayjs(date).format(globalDateFormat) : "");

/**
 * Gets the given date at the given time zone, ensuring we just get the date portion.
 * E.g. if given "12/22/2021 8:30 AM", will get "2021-12-22 00:00:00 -XX:XX" regardless of
 * what the current browser time zone and given time zone are. The trailing offset will
 * be for the given FC time zone, or for the browser time zone if not specified.
 *
 * @date The date and time to extract date from. Defaults to current browser date and time if null.
 * @fcTimeZone The Fulfillment Center's time zone, if specified. Will default to using browser time zone if not specified.
 */
export const getDateForTimeZone = (
  date: Date | null,
  fcTimeZone: string | undefined
): string => {
  // Format for getting midnight of the given date in ISO 8601 format. E.g. "2022-12-21 00:00:00"
  const isoDateFormat = "YYYY-MM-DD 00:00:00";
  const dateToUse = dayjs(date || new Date());

  const dateWithTimeZone = fcTimeZone
    ? dayjs.tz(dateToUse.format(isoDateFormat), fcTimeZone)
    : dateToUse;

  const formattedDate = dateWithTimeZone.format(`${isoDateFormat} Z`);

  return formattedDate;
};

/** Check if the given date is before today's date. */
export const isPastDate = (date: Date | null) => {
  const today = dayjs().startOf("day");
  return dayjs(date).isBefore(today);
};

/** Check if the given date/time is in the past. */
export const isPastTime = (deadlineDate: Date, timeZone?: string | null) => {
  const deadline = dayjs(deadlineDate).tz(timeZone || defaultTimezone);
  return deadline.isBefore(dayjs().tz(timeZone || defaultTimezone));
};

/** Is the given date before or equal to the expiration date? */
export const isExpired = (date: Dayjs | Date, expirationDate: Dayjs | null) => {
  if (!expirationDate) return false;

  const endOfDate = dayjs(date).endOf("day");

  const isExpired = expirationDate.isSameOrBefore(endOfDate);

  return isExpired;
};

/**
 * Converts a schedule window string to a dayjs object.
 *
 * @param scheduleWindow - A string representing time in the format "HH:MM:SS"
 *
 * @returns {Dayjs} A dayjs object set to the current date with the time
 * specified in the input string.
 */
export const getDayjsFromScheduleWindow = (scheduleWindow: string): Dayjs => {
  const [hours, minutes, seconds] = scheduleWindow.split(":");
  return dayjs().hour(+hours).minute(+minutes).second(+seconds);
};
